import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";

import {
  SlotRegistrationStatus,
  StoreRegistrationItem,
  UpdateRegistrationStatusDto,
  updateRegistrationStatusApi,
  RegistrationPayment,
  UpdateRegistrationPaymentDto,
  getRegistrationPaymentApi,
  updateRegistrationPaymentApi,
} from "../../../views/scheduling/schedulingApi";

import type { StorePublic } from "../../../views/store/storeApi";

import "../update-presence-and-values/UpdatePresenceAndValuesModal.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  store: StorePublic;
  startDayYmd: string;
  endDayYmd: string;
  registration: StoreRegistrationItem;
  onSaved?: () => void;
};

function formatBrDate(ymd: string): string {
  const [y, m, d] = (ymd ?? "").split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
}

function formatTimeFromIso(iso: string): string {
  return (iso ?? "").slice(11, 16);
}

function formatBRL(v: string | null | undefined) {
  if (!v) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

function safeStatusLabel(s: string) {
  return (s ?? "").toUpperCase();
}

function normalizeRangeYmd(startYmd: string, endYmd: string) {
  if (startYmd <= endYmd) {
    return { startDay: startYmd, endDay: endYmd };
  }

  return { startDay: endYmd, endDay: startYmd };
}

const PRESENCE_STATUS_OPTIONS: SlotRegistrationStatus[] = [
  "PENDENTE",
  "APROVADO",
  "AUSENTE",
  "CANCELADO",
];

const UpdatePresenceAndValuesTableModal: React.FC<Props> = ({
  isOpen,
  onClose,
  store,
  startDayYmd,
  endDayYmd,
  registration,
  onSaved,
}) => {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [loadingPayment, setLoadingPayment] = useState(false);
  const [payment, setPayment] = useState<RegistrationPayment | null>(null);

  const [presenceStatus, setPresenceStatus] = useState<SlotRegistrationStatus>("PENDENTE");
  const [afiliacao, setAfiliacao] = useState<"ECO" | "ZE" | "">("");

  const [extraHours, setExtraHours] = useState<number>(0);
  const [extraNote, setExtraNote] = useState<string>("");

  const [earlyLeaveHours, setEarlyLeaveHours] = useState<number>(0);
  const [earlyLeaveNote, setEarlyLeaveNote] = useState<string>("");

  const [arbitraryValue, setArbitraryValue] = useState<string>("0.00");
  const [arbitraryNote, setArbitraryNote] = useState<string>("");

  const [saving, setSaving] = useState(false);

  const valuesEnabled = useMemo(() => presenceStatus === "APROVADO", [presenceStatus]);

  const selectedRange = useMemo(
    () => normalizeRangeYmd(startDayYmd, endDayYmd),
    [startDayYmd, endDayYmd],
  );

  const registrationDayYmd = useMemo(
    () => String((registration as any)?.day ?? "").slice(0, 10),
    [registration],
  );

  const resetValues = useCallback(() => {
    setPayment(null);
    setExtraHours(0);
    setExtraNote("");
    setEarlyLeaveHours(0);
    setEarlyLeaveNote("");
    setArbitraryValue("0.00");
    setArbitraryNote("");
  }, []);

  const fillFormFromPayment = useCallback((p: RegistrationPayment) => {
    setPayment(p);

    setExtraHours(p.extraHours ?? 0);
    setExtraNote(p.extraNote ?? "");

    setEarlyLeaveHours(p.earlyLeaveHours ?? 0);
    setEarlyLeaveNote(p.earlyLeaveNote ?? "");

    setArbitraryValue(p.arbitraryValue ?? "0.00");
    setArbitraryNote(p.arbitraryNote ?? "");
  }, []);

  const fetchPayment = useCallback(
    async (registrationId: string) => {
      if (!token) return;
      try {
        setLoadingPayment(true);
        const p = await getRegistrationPaymentApi(registrationId, token);
        fillFormFromPayment(p);
      } catch {
        setPayment(null);
      } finally {
        setLoadingPayment(false);
      }
    },
    [token, fillFormFromPayment],
  );

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const regStatus = ((registration.status ?? "PENDENTE") as SlotRegistrationStatus) ?? "PENDENTE";
    setPresenceStatus(regStatus);
    setAfiliacao(((registration as any).freelancer?.afiliacao as any) ?? "");

    if (safeStatusLabel(regStatus) === "APROVADO") {
      fetchPayment(registration.id);
    } else {
      resetValues();
    }
  }, [isOpen, registration, fetchPayment, resetValues]);

  const buildPaymentDto = useCallback((): UpdateRegistrationPaymentDto => {
    const dto: UpdateRegistrationPaymentDto = {};

    dto.extraHours = Number.isFinite(extraHours) ? extraHours : 0;
    dto.extraNote = extraNote?.trim() ? extraNote.trim() : undefined;

    dto.earlyLeaveHours = Number.isFinite(earlyLeaveHours) ? earlyLeaveHours : 0;
    dto.earlyLeaveNote = earlyLeaveNote?.trim() ? earlyLeaveNote.trim() : undefined;

    if (`${arbitraryValue}`.trim() !== "") dto.arbitraryValue = `${arbitraryValue}`.trim();
    dto.arbitraryNote = arbitraryNote?.trim() ? arbitraryNote.trim() : undefined;

    if (afiliacao) (dto as any).afiliacao = afiliacao;

    return dto;
  }, [extraHours, extraNote, earlyLeaveHours, earlyLeaveNote, arbitraryValue, arbitraryNote, afiliacao]);

  const handleSave = useCallback(async () => {
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      onClose();
      return;
    }

    try {
      setSaving(true);

      const targetStatus = (presenceStatus ?? "PENDENTE") as SlotRegistrationStatus;
      const currentStatus = ((registration.status ?? "PENDENTE") as SlotRegistrationStatus) ?? "PENDENTE";
      const statusChanged = safeStatusLabel(currentStatus) !== safeStatusLabel(targetStatus);

      if (statusChanged) {
        const dto: UpdateRegistrationStatusDto = { status: targetStatus };
        await updateRegistrationStatusApi(registration.id, token, dto);
      }

      if (targetStatus === "APROVADO") {
        const paymentDto = buildPaymentDto();
        const updatedPayment = await updateRegistrationPaymentApi(registration.id, token, paymentDto);
        setPayment(updatedPayment);
      } else {
        resetValues();
      }

      toast.success("Atualização realizada com sucesso.");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Erro ao salvar alterações. Verifique permissões e dados.");
    } finally {
      setSaving(false);
    }
  }, [token, onClose, onSaved, registration.id, registration.status, presenceStatus, buildPaymentDto, resetValues]);

  const registrationLabel = useMemo(() => {
    const t1 = formatTimeFromIso(registration.slot?.startAt);
    const t2 = formatTimeFromIso(registration.slot?.endAt);
    const regDayLabel = registrationDayYmd ? `${formatBrDate(registrationDayYmd)} • ` : "";
    return `${registration.freelancer?.nomeCompleto ?? "-"} • ${regDayLabel}${t1}-${t2} • ${safeStatusLabel(
      registration.status,
    )}`;
  }, [registration, registrationDayYmd]);

  const registrationValueLabel = useMemo(() => {
    const status = safeStatusLabel(presenceStatus ?? registration.status);

    if (status !== "APROVADO") return "Sem Valor Definido";

    const raw =
      payment?.totalValue ??
      (registration as any)?.finalValue ??
      (registration as any)?.valorFinal ??
      (registration as any)?.payment?.totalValue ??
      (registration as any)?.payment?.finalValue ??
      (registration as any)?.paymentFinalValue ??
      null;

    const formatted = formatBRL(raw == null ? null : String(raw));
    return formatted || "-";
  }, [payment, presenceStatus, registration]);

  if (!isOpen) return null;

  const handleOverlayClick = () => onClose();
  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => e.stopPropagation();

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Atualizar informações de presença e valores</h2>

        <div className="ModalBody">
          <div className="SelectedInfoBox">
            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Loja</span>
              <span className="SelectedInfoValue">{store.nomeFantasia}</span>
            </div>

            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Período</span>
              <span className="SelectedInfoValue">
                {formatBrDate(selectedRange.startDay)} até {formatBrDate(selectedRange.endDay)}
              </span>
            </div>

            {registrationDayYmd ? (
              <div className="SelectedInfoRow">
                <span className="SelectedInfoLabel">Dia da inscrição</span>
                <span className="SelectedInfoValue">{formatBrDate(registrationDayYmd)}</span>
              </div>
            ) : null}

            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Inscrição</span>
              <span className="SelectedInfoValue">{registrationLabel}</span>
            </div>

            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Valor</span>
              <span className="SelectedInfoValue">{registrationValueLabel}</span>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Avaliação</div>

            <div className="PVFormGrid">
              <div className="PVField">
                <div className="PVLabelRow">
                  <label className="PVLabel">Afiliação</label>

                  <select
                    className="PVInput PVInputInline"
                    value={afiliacao}
                    onChange={(e) => setAfiliacao(e.target.value as any)}
                    disabled={saving}
                  >
                    <option value="">Selecione</option>
                    <option value="ECO">Eco</option>
                    <option value="ZE">Zé</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Presença</div>

            <div className="PVFormGrid">
              <div className="PVField">
                <div className="PVLabelRow">
                  <label className="PVLabel">Status</label>

                  <select
                    className="PVInput PVInputInline"
                    value={presenceStatus}
                    onChange={(e) => setPresenceStatus(e.target.value as any)}
                    disabled={saving}
                  >
                    {PRESENCE_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="PVHint">
                  Ao marcar <b>APROVADO</b>, o sistema gera o pagamento base (R$ 130) automaticamente. <br />
                  Ao marcar qualquer outra opção, o sistema <b>zera todos os valores</b>.
                </div>
              </div>

              <div className="PVField">
                <div className="PVLabelRow">
                  <label className="PVLabel">Resumo do pagamento</label>

                  <div className="PVSummary PVSummaryInline">
                    {loadingPayment ? (
                      <span>Carregando...</span>
                    ) : presenceStatus !== "APROVADO" ? (
                      <span className="PVMuted">Valores disponíveis apenas para APROVADO.</span>
                    ) : payment ? (
                      <span>
                        Base: <b>R$ {payment.baseValue}</b>
                        <br />
                        Total atual: <b>R$ {payment.totalValue}</b>
                      </span>
                    ) : (
                      <span className="PVMuted">
                        Ainda não há pagamento carregado. Ao salvar, será gerado e calculado.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Valores e observações</div>

            {!valuesEnabled && (
              <div className="PVAlert">
                Para lançar horas/valores, primeiro deixe o status como <b>APROVADO</b>.
              </div>
            )}

            <div className={`PVFormGrid ${!valuesEnabled ? "PVDisabled" : ""}`}>
              <div className="PVField">
                <div className="PVLabelRow">
                  <label className="PVLabel">Hora extra (horas)</label>
                  <input
                    className="PVInput PVInputInline"
                    type="number"
                    min={0}
                    step={1}
                    value={extraHours}
                    onChange={(e) => setExtraHours(Number(e.target.value))}
                    disabled={!valuesEnabled || saving}
                  />
                </div>

                <label className="PVLabel">Observação (hora extra)</label>
                <textarea
                  className="PVTextarea"
                  value={extraNote}
                  onChange={(e) => setExtraNote(e.target.value)}
                  disabled={!valuesEnabled || saving}
                  placeholder="Ex.: Inventário estendeu por atraso..."
                />
              </div>

              <div className="PVField">
                <div className="PVLabelRow">
                  <label className="PVLabel">Saída antecipada (horas)</label>
                  <input
                    className="PVInput PVInputInline"
                    type="number"
                    min={0}
                    step={1}
                    value={earlyLeaveHours}
                    onChange={(e) => setEarlyLeaveHours(Number(e.target.value))}
                    disabled={!valuesEnabled || saving}
                  />
                </div>

                <label className="PVLabel">Observação (saída antecipada)</label>
                <textarea
                  className="PVTextarea"
                  value={earlyLeaveNote}
                  onChange={(e) => setEarlyLeaveNote(e.target.value)}
                  disabled={!valuesEnabled || saving}
                  placeholder="Ex.: Loja fechou mais cedo..."
                />
              </div>

              <div className="PVField PVFieldFull">
                <div className="PVLabelRow">
                  <label className="PVLabel">Valor arbitrário (R$)</label>

                  <input
                    className="PVInput PVInputInlineMoney"
                    type="number"
                    value={arbitraryValue}
                    onChange={(e) => setArbitraryValue(e.target.value)}
                    disabled={!valuesEnabled || saving}
                    placeholder='Ex.: "25.50" (pode ser "-10.00")'
                  />
                </div>

                <label className="PVLabel">Observação (valor arbitrário)</label>
                <textarea
                  className="PVTextarea"
                  value={arbitraryNote}
                  onChange={(e) => setArbitraryNote(e.target.value)}
                  disabled={!valuesEnabled || saving}
                  placeholder="Ex.: Reembolso Uber..."
                />
              </div>
            </div>
          </div>

          <div className="ModalActions">
            <button
              type="button"
              className="ModalButton ModalButtonCancel"
              onClick={onClose}
              disabled={saving}
            >
              Fechar
            </button>

            <button
              type="button"
              className="ModalButton ModalButtonPrimary"
              onClick={handleSave}
              disabled={saving}
              title={saving ? "Salvando..." : "Salvar alterações"}
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePresenceAndValuesTableModal;
